import React, { useCallback, useMemo, useState } from "react";
import { getAnonymousAccountNumber } from "@common/helpers/bankAccount";
import { Entity } from "@common/types/entity";
import { FinancialAccount } from "@common/types/financialAccount";
import { AccountPickerIcon } from "@components/financialAccount/AccountPickerItem";
import { FilterPickerListActions } from "@components/pageHeader/filters/FilterPickerList/FilterPickerListParts";
import type {
    TooltipTriggerButtonProps,
    TriggerButtonProps,
} from "@design-system/overlay/triggerButtonProps";
import type { ValueControlProps } from "@design-system/formControl/formControl.types";
import {
    type IdPickerValueProps,
    pickSingleId,
    toIdArray,
} from "@design-system/formControl/idPickerValue";
import { HStack } from "@design-system/HStack/HStack";
import {
    Picker,
    pickerTreeRowPlacements,
} from "@design-system/Picker/Picker.lib";
import { getSubaccountDisplayName } from "@design-system/Subaccount/utils";
import { useEntities } from "@hooks/useEntities";
import {
    useFinancialAccounts,
    UseFinancialAccountsOptions,
} from "@hooks/useFinancialAccounts";
import { AccountsFilterAccountValueDisplay } from "./AccountsFilterAccountValueDisplay";
import {
    accountMatchesSearch,
    getSubaccountIdsForAccounts,
    toItemKey,
} from "./FinancialAccountCombobox.lib";
import styles from "./FinancialAccountCombobox.module.scss";

interface EntityWithAccounts extends Entity {
    accounts: FinancialAccount[];
}

interface FinancialAccountComboboxOwnProps
    extends
        Omit<UseFinancialAccountsOptions, "isSingleSelect">,
        Pick<
            ValueControlProps<number | null, HTMLButtonElement>,
            "onBlur" | "invalid" | "disabled" | "ref"
        > {
    subaccountIds?: string[];
    onSubaccountChange?: (subaccountIds: string[]) => void;
    buttonSize?: TriggerButtonProps["size"];
    buttonProps?: TooltipTriggerButtonProps;
    hideArrow?: boolean;
    container?: HTMLElement;
    accountFilterPredicate?: (account: FinancialAccount) => boolean;
    isBusinessOnly?: boolean;
    onOpenChange?: (open: boolean) => void;
}

/**
 * Financial account picker: one account id (or `null`) by default, an id
 * array with `multiple`. Subaccounts are picked in multiple mode only. Rows
 * are checkboxes in both modes, so a single pick is unticked (to `null`) by
 * choosing it again.
 */
export type FinancialAccountComboboxProps = FinancialAccountComboboxOwnProps &
    IdPickerValueProps<number>;

const accountKey = (accountId: number) =>
    toItemKey({ kind: "account", accountId });
const subaccountKey = (subaccountId: string) =>
    toItemKey({ kind: "subaccount", subaccountId });

/** Legacy `AccountCard onlyNumber`: a small "••" and the last four, else the name. */
const AccountRowLabel = ({ account }: { account: FinancialAccount }) =>
    account.accountNumberMask ? (
        <span className={styles.accountNumber}>
            <span className={styles.accountMask}>••</span>
            {getAnonymousAccountNumber(account.accountNumberMask, 0)}
        </span>
    ) : (
        (account.name ?? "")
    );

export const FinancialAccountCombobox: React.FC<
    FinancialAccountComboboxProps
> = ({
    value,
    onValueChange,
    multiple,
    subaccountIds,
    onSubaccountChange,
    onBlur,
    invalid,
    disabled,
    ref,
    buttonSize = "md",
    buttonProps,
    hideArrow = true,
    excludedAccountIds,
    container,
    accountFilterPredicate,
    isBusinessOnly,
    onOpenChange,
    ...financialAccountOptions
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const entities = useEntities();
    const allBankAccounts = useFinancialAccounts({
        excludedAccountIds,
        ...financialAccountOptions,
        onlyBusiness: isBusinessOnly ?? financialAccountOptions.onlyBusiness,
    });

    const bankAccounts = useMemo(
        () =>
            accountFilterPredicate
                ? allBankAccounts.filter(accountFilterPredicate)
                : allBankAccounts,
        [allBankAccounts, accountFilterPredicate],
    );

    const accountIds = useMemo(() => toIdArray(value), [value]);
    const selectedAccountSet = useMemo(() => new Set(accountIds), [accountIds]);
    const selectedSubaccountSet = useMemo(
        () => new Set(subaccountIds ?? []),
        [subaccountIds],
    );

    const allEntitiesWithAccounts = useMemo(
        (): EntityWithAccounts[] =>
            entities
                .map((entity) => ({
                    ...entity,
                    accounts: bankAccounts.filter(
                        (account) => account.entity?.id === entity.id,
                    ),
                }))
                .filter(
                    ({ accounts, isBusiness }) =>
                        accounts.length > 0 && (!isBusinessOnly || isBusiness),
                ),
        [bankAccounts, entities, isBusinessOnly],
    );

    const entitiesWithAccounts = useMemo(
        (): EntityWithAccounts[] =>
            allEntitiesWithAccounts
                .map((entity) => ({
                    ...entity,
                    accounts: entity.accounts.filter((account) =>
                        accountMatchesSearch(account, search),
                    ),
                }))
                .filter(({ accounts }) => accounts.length > 0),
        [allEntitiesWithAccounts, search],
    );

    const selectedKeys = useMemo(
        () => [
            ...accountIds.map(accountKey),
            ...(subaccountIds ?? []).map(subaccountKey),
        ],
        [accountIds, subaccountIds],
    );

    const { accountIdByKey, subaccountIdByKey } = useMemo(() => {
        const knownAccountIds = new Set([
            ...accountIds,
            ...bankAccounts.map(({ id }) => id),
        ]);
        const knownSubaccountIds = new Set([
            ...(subaccountIds ?? []),
            ...getSubaccountIdsForAccounts(bankAccounts),
        ]);
        return {
            accountIdByKey: new Map(
                Array.from(knownAccountIds, (id) => [accountKey(id), id]),
            ),
            subaccountIdByKey: new Map(
                Array.from(knownSubaccountIds, (id) => [subaccountKey(id), id]),
            ),
        };
    }, [accountIds, subaccountIds, bankAccounts]);

    const handleValueChange = useCallback(
        (nextKeys: string[]) => {
            if (disabled) {
                return;
            }
            const nextAccountIds = nextKeys.flatMap((key) => {
                const accountId = accountIdByKey.get(key);
                return accountId === undefined ? [] : [accountId];
            });

            if (!multiple) {
                const accountId = pickSingleId({
                    previousIds: accountIds,
                    nextIds: nextAccountIds,
                });
                onValueChange(accountId);
                if (accountId !== null) {
                    setIsOpen(false);
                }
                return;
            }

            // Unticking a whole entity also drops its accounts' subaccounts.
            const nextAccountSet = new Set(nextAccountIds);
            const clearedSubaccountIds = new Set(
                getSubaccountIdsForAccounts(
                    allEntitiesWithAccounts
                        .filter(({ accounts }) =>
                            accounts.every(
                                ({ id }) =>
                                    selectedAccountSet.has(id) &&
                                    !nextAccountSet.has(id),
                            ),
                        )
                        .flatMap(({ accounts }) => accounts),
                ),
            );
            const nextSubaccountIds = nextKeys.flatMap((key) => {
                const subaccountId = subaccountIdByKey.get(key);
                return subaccountId === undefined ||
                    clearedSubaccountIds.has(subaccountId)
                    ? []
                    : [subaccountId];
            });
            onValueChange(nextAccountIds);
            onSubaccountChange?.(nextSubaccountIds);
        },
        [
            disabled,
            accountIdByKey,
            subaccountIdByKey,
            multiple,
            accountIds,
            onValueChange,
            allEntitiesWithAccounts,
            selectedAccountSet,
            onSubaccountChange,
        ],
    );

    const reverseSelection = useCallback(() => {
        if (!multiple) {
            return;
        }
        onValueChange(
            bankAccounts
                .filter((account) => !selectedAccountSet.has(account.id))
                .map(({ id }) => id),
        );
        onSubaccountChange?.([]);
    }, [
        multiple,
        onValueChange,
        bankAccounts,
        selectedAccountSet,
        onSubaccountChange,
    ]);

    const clearSelection = useCallback(() => {
        if (!multiple) {
            return;
        }
        onValueChange([]);
        onSubaccountChange?.([]);
    }, [multiple, onValueChange, onSubaccountChange]);

    const selectedCount = accountIds.length + (subaccountIds?.length ?? 0);
    const hasFooter = multiple === true && selectedCount > 0;
    const { tooltip: buttonTooltip, ...triggerButtonProps } = buttonProps ?? {};

    return (
        <Picker.Select<string, true>
            multiple
            searchable
            value={selectedKeys}
            onValueChange={handleValueChange}
            open={isOpen}
            onOpenChange={(open) => {
                setIsOpen(open);
                if (!open) {
                    setSearch("");
                }
                onOpenChange?.(open);
            }}
            inputValue={search}
            onInputValueChange={setSearch}
            onBlur={onBlur}
            aria-invalid={invalid}
            disabled={disabled}
            container={container}
        >
            <Picker.ButtonTrigger
                ref={ref}
                render={
                    <Picker.Button
                        variant="primary"
                        disabled={disabled}
                        {...triggerButtonProps}
                        size={triggerButtonProps.size ?? buttonSize}
                    />
                }
                value={
                    <HStack gap={8} className={styles.triggerLabel}>
                        <AccountsFilterAccountValueDisplay
                            isAccountSelected={(accountId) =>
                                selectedAccountSet.has(accountId)
                            }
                            isSubaccountSelected={(subaccountId) =>
                                selectedSubaccountSet.has(subaccountId)
                            }
                            showSelectedIcons
                            isMultiple={multiple === true}
                        />
                    </HStack>
                }
                tooltip={buttonTooltip}
                showChevron={!hideArrow}
            />
            <Picker.Popup sideOffset={4}>
                <Picker.Search placeholder="Search accounts…" />
                <Picker.List>
                    {entitiesWithAccounts.length === 0 && (
                        <Picker.Empty>No matching accounts</Picker.Empty>
                    )}
                    {entitiesWithAccounts.map((entity, entityIndex) => (
                        <FinancialAccountComboboxSection
                            key={entity.id}
                            entity={entity}
                            entityAccounts={
                                allEntitiesWithAccounts.find(
                                    ({ id }) => id === entity.id,
                                )?.accounts ?? entity.accounts
                            }
                            hasSeparator={entityIndex > 0}
                            hasSelectAll={multiple === true}
                        />
                    ))}
                </Picker.List>
                {hasFooter && (
                    <Picker.Footer>
                        <FilterPickerListActions
                            selectedCount={selectedCount}
                            onDeselect={clearSelection}
                            onReverse={reverseSelection}
                        />
                    </Picker.Footer>
                )}
            </Picker.Popup>
        </Picker.Select>
    );
};

interface FinancialAccountComboboxSectionProps {
    /** The entity with the accounts the search leaves visible. */
    entity: EntityWithAccounts;
    /** Every account of the entity, which its select-all toggles. */
    entityAccounts: FinancialAccount[];
    hasSeparator: boolean;
    hasSelectAll: boolean;
}

/** One entity: its title (with select-all in multiple mode), accounts and their subaccounts. */
const FinancialAccountComboboxSection = ({
    entity,
    entityAccounts,
    hasSeparator,
    hasSelectAll,
}: FinancialAccountComboboxSectionProps) => {
    const rows = entity.accounts.flatMap((account) => [
        { depth: 0, account, subaccount: undefined },
        ...(account.subaccounts ?? []).map((subaccount) => ({
            depth: 1,
            account,
            subaccount,
        })),
    ]);
    const placements = pickerTreeRowPlacements(rows.map(({ depth }) => depth));

    return (
        <>
            {hasSeparator && <Picker.Separator />}
            <Picker.Section>
                <Picker.SectionTitle<string>
                    selectAll={
                        hasSelectAll
                            ? {
                                  values: entityAccounts.map(({ id }) =>
                                      accountKey(id),
                                  ),
                              }
                            : undefined
                    }
                >
                    {entity.name}
                </Picker.SectionTitle>
                {rows.map(({ depth, account, subaccount }, rowIndex) =>
                    subaccount ? (
                        <Picker.Item
                            key={subaccount.id}
                            value={subaccountKey(subaccount.id)}
                            depth={depth}
                            treePosition={placements[rowIndex].treePosition}
                            ancestorLines={placements[rowIndex].ancestorLines}
                        >
                            {getSubaccountDisplayName(subaccount)}
                        </Picker.Item>
                    ) : (
                        <Picker.Item
                            key={account.id}
                            value={accountKey(account.id)}
                            leading={<AccountPickerIcon account={account} />}
                            tooltip={
                                account.accountNumberMask && account.name
                                    ? account.name
                                    : undefined
                            }
                        >
                            <AccountRowLabel account={account} />
                        </Picker.Item>
                    ),
                )}
            </Picker.Section>
        </>
    );
};
